import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/authStore';
import { getRedirectIntent } from '../../utils/authRedirect';
import { useTranslation } from 'react-i18next';
import { CURRENT_TOS_VERSION } from '../../config/tos';
import { UserType } from '../../constants/userTypes';
import { OAuthButton } from './OAuthButton';
import GlobalNavBar from '../layout/GlobalNavBar';

interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  userType: UserType;
  tosAccepted: boolean;
  marketingConsent: boolean;
}

export const RegisterForm: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const navigate = useNavigate();
  const register = useAuthStore((state) => state.register);
  const { t } = useTranslation('auth');

  const {
    register: registerField,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>();

  const watchPassword = watch('password');
  const watchUserType = watch('userType');
  const watchTosAccepted = watch('tosAccepted');
  const watchMarketingConsent = watch('marketingConsent');

  // Determine if OAuth buttons should be shown
  const showOAuthButtons = true; // watchUserType && watchTosAccepted;

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setOauthError(null);
    try {
      await register(
        data.email,
        data.password,
        data.name,
        data.userType,
        CURRENT_TOS_VERSION,
        Date.now(),
        data.marketingConsent
      );
      
      // Get redirect intent to pass through to confirmation
      const redirectIntent = getRedirectIntent();
      
      navigate('/confirm-email', {
        state: {
          email: data.email,
          message: 'Please check your email for confirmation code.',
          redirectPath: redirectIntent?.path || undefined
        }
      });
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthError = (error: Error) => {
    console.error('OAuth error:', error);
    setOauthError(error.message);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNavBar variant="dark" maxWidth="max-w-7xl" />

      {/* Content */}
      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth:createYourAccount')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('auth:alreadyHaveAccount')}{' '}
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              {t('auth:signIn')}
            </Link>
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                {t('auth:fullName')}
              </label>
              <input
                {...registerField('name', {
                  required: t('auth:errors.nameRequired'),
                  minLength: {
                    value: 2,
                    message: t('auth:errors.nameMinLength'),
                  },
                })}
                type="text"
                className="mt-1 input"
                placeholder={t('auth:enterFullName')}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('auth:emailAddress')}
              </label>
              <input
                {...registerField('email', {
                  required: t('auth:errors.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('auth:errors.invalidEmail'),
                  },
                })}
                type="email"
                autoComplete="email"
                className="mt-1 input"
                placeholder={t('auth:enterEmail')}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="userType" className="block text-sm font-medium text-gray-700">
                {t('auth:accountType')}
              </label>
              <select
                {...registerField('userType', {
                  required: t('auth:errors.accountTypeRequired'),
                })}
                className="mt-1 input"
              >
                <option value="">{t('auth:selectAccountType')}</option>
                <option value="brand">{t('auth:brandHost')}</option>
                <option value="agency">{t('auth:agency')}</option>
                <option value="influencer">{t('auth:influencerCreator')}</option>
              </select>
              {errors.userType && (
                <p className="mt-1 text-sm text-red-600">{errors.userType.message}</p>
              )}
            </div>

            <div className="relative">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('auth:password')}
              </label>
              <input
                {...registerField('password', {
                  required: t('auth:errors.passwordRequired'),
                  minLength: {
                    value: 8,
                    message: t('auth:errors.passwordMinLength'),
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                    message: t('auth:errors.passwordComplexity'),
                  },
                })}
                type={showPassword ? 'text' : 'password'}
                className="mt-1 input pr-10"
                placeholder={t('auth:createPassword')}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                style={{ top: '1.75rem' }}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            <div className="relative">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                {t('auth:confirmPassword')}
              </label>
              <input
                {...registerField('confirmPassword', {
                  required: t('auth:errors.confirmPasswordRequired'),
                  validate: (value) =>
                    value === watchPassword || t('auth:errors.passwordMismatch'),
                })}
                type={showConfirmPassword ? 'text' : 'password'}
                className="mt-1 input pr-10"
                placeholder={t('auth:confirmYourPassword')}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                style={{ top: '1.75rem' }}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Terms of Service Acceptance */}
            <div className="space-y-3">
              <div className="flex items-start">
                <input
                  {...registerField('tosAccepted', {
                    required: t('auth:errors.tosRequired'),
                  })}
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="tosAccepted" className="ml-2 block text-sm text-gray-700">
                  {t('auth:tosAcceptance')}{' '}
                  <Link
                    to="/legal/terms-of-service"
                    target="_blank"
                    className="text-primary-600 hover:text-primary-500 underline"
                  >
                    {t('auth:termsOfService')}
                  </Link>
                  {' '}{t('auth:and')}{' '}
                  <Link
                    to="/legal/privacy-policy"
                    target="_blank"
                    className="text-primary-600 hover:text-primary-500 underline"
                  >
                    {t('auth:privacyPolicy')}
                  </Link>
                </label>
              </div>
              {errors.tosAccepted && (
                <p className="ml-6 text-sm text-red-600">{errors.tosAccepted.message}</p>
              )}

              {/* Marketing Consent (Optional) */}
              <div className="flex items-start">
                <input
                  {...registerField('marketingConsent')}
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="marketingConsent" className="ml-2 block text-sm text-gray-700">
                  {t('auth:marketingConsent')}
                </label>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? t('auth:creatingAccount') : t('auth:createAccount')}
            </button>
          </div>
        </form>

        {/* OAuth Error Display */}
        {oauthError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{oauthError}</p>
          </div>
        )}

        {/* Visual Separator - Only show if OAuth buttons will be displayed */}
        {showOAuthButtons && (
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 text-gray-500">
                  {t('auth:or')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* OAuth Buttons - Only show after user type and ToS acceptance */}
        {showOAuthButtons && (
          <div className="mt-6 space-y-3">
            <OAuthButton
              provider="google"
              mode="signup"
              userType={watchUserType}
              tosData={{
                version: CURRENT_TOS_VERSION,
                acceptedAt: new Date().toISOString(),
                marketingConsent: watchMarketingConsent || false,
              }}
              onError={handleOAuthError}
            />
            {/* Naver is hidden in production until the provider review passes. */}
            {process.env.NODE_ENV !== 'production' && (
              <OAuthButton
                provider="naver"
                mode="signup"
                userType={watchUserType}
                tosData={{
                  version: CURRENT_TOS_VERSION,
                  acceptedAt: new Date().toISOString(),
                  marketingConsent: watchMarketingConsent || false,
                }}
                onError={handleOAuthError}
              />
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};
